import {
  DURABLE_OPTIONS_CANONICAL,
  RemoteWorkflowExecutor,
  STATE_STORE_CANONICAL,
  type StateStore,
  type WorkflowCtx,
  WorkflowEngine,
  type WorkflowRun,
  bindWorkflowClass,
  parseDuration,
  sanitizeQueueToken,
  tenantGroup,
  workflowName,
} from '@dudousxd/nestjs-durable-core';
import {
  Inject,
  Injectable,
  type OnApplicationBootstrap,
  type OnApplicationShutdown,
  type OnModuleInit,
} from '@nestjs/common';
import { DiscoveryService, MetadataScanner } from '@nestjs/core';
import { getOnEvents, isDeadLetterHandler } from './decorators';
import { scanWorkflows } from './discovery-helpers';
import type { DurableModuleOptions } from './durable.module';
import { entityConfigFor, getEntityMeta } from './entity';
import { IN_APP_WORKER_BINDING, type InAppWorkerBinding } from './in-app-worker';
import { classValidatorInput } from './input-validation';
import { isDrivingOperator, isOperatorRole } from './role';
import { type DurableStepInterceptor, isStepInterceptor } from './step-interceptor';

type WorkflowFn = (ctx: WorkflowCtx, input: unknown) => Promise<unknown>;

/**
 * Ensures the schema (auto-schema) and registers `@Workflow` providers on init, resumes runs left
 * incomplete by a previous process once booted, and drains in-flight runs on shutdown.
 *
 * For the shutdown drain to fire, enable Nest's shutdown hooks: `app.enableShutdownHooks()`.
 */
@Injectable()
export class WorkflowRegistrar
  implements OnModuleInit, OnApplicationBootstrap, OnApplicationShutdown
{
  constructor(
    private readonly discovery: DiscoveryService,
    private readonly metadataScanner: MetadataScanner,
    private readonly engine: WorkflowEngine,
    @Inject(STATE_STORE_CANONICAL) private readonly store: StateStore | null,
    @Inject(DURABLE_OPTIONS_CANONICAL) private readonly options: DurableModuleOptions,
    // Group-served binding when the app opted into an in-app worker (uniform dispatch); null = inline.
    @Inject(IN_APP_WORKER_BINDING) private readonly inAppWorker: InAppWorkerBinding | null,
  ) {}

  async onApplicationBootstrap(): Promise<void> {
    // Recover runs left incomplete by a crash/deploy when this operator instance is DRIVING
    // (defaults to true). A non-driving (dashboard-only) operator, or a thin worker with no `store`
    // at all, must not pick up and re-run workflows — leave that to a driving instance.
    if (!isDrivingOperator(this.options)) return;
    await this.engine.recoverIncomplete();
  }

  /** On deploy/shutdown: stop picking up new runs and wait for in-flight ones to settle, then close
   *  the transport(s) so the broker workers stop consuming and connections are released. Closing
   *  AFTER the drain keeps the transport alive while in-flight runs dispatch/await their remote steps.
   *  Operator only — a thin worker (no `store`) holds no engine to drain. */
  async onApplicationShutdown(): Promise<void> {
    if (!isOperatorRole(this.options)) return;
    await this.engine.drain(this.options.shutdownTimeoutMs);
    const transports = [
      this.options.transport,
      ...(this.options.transports ?? []).map((t) => t.transport),
    ];
    await Promise.allSettled(transports.map((t) => t?.close?.()));
  }

  async onModuleInit(): Promise<void> {
    // Operator only — a thin worker (no `store`) registers its bodies on a `DurableWorkerRuntime`
    // instead (see `durable-worker.module.ts`'s `ThinWorkflowRegistrar`), never on this engine.
    if (!isOperatorRole(this.options)) return;
    const store = this.store;
    if (!store) {
      throw new Error(
        'unreachable: STATE_STORE_CANONICAL must resolve a store when options.store is set',
      );
    }
    if (this.options.autoSchema !== false) {
      await store.ensureSchema?.();
    }
    // Maps a workflow name to the workflow its dead runs route to. Built from each `@Workflow`'s
    // inline `@DeadLetter()` method (preferred) or its `deadLetterWorkflow` reference; the
    // module-level `deadLetterWorkflow` is the fallback applied in the onDead listener below.
    const deadLetterByWorkflow = new Map<string, string>();

    // Pre-pass: wire interceptors and register @Entity providers (engine-only concerns).
    for (const wrapper of this.discovery.getProviders()) {
      const { instance } = wrapper;
      if (!instance || typeof instance !== 'object') continue;
      if (isStepInterceptor(instance.constructor)) {
        const interceptor = instance as DurableStepInterceptor;
        this.engine.use((invocation, next) => interceptor.intercept(invocation, next));
      }
      const entityMeta = getEntityMeta(instance.constructor);
      if (entityMeta) {
        this.engine.registerEntity(entityMeta.name, entityConfigFor(instance.constructor));
      }
    }

    // Workflows whose declaring package could not be derived — reported once, after the scan.
    const unattributed: string[] = [];

    // Workflow registration pass — shared scan, engine-specific registration callback.
    scanWorkflows(this.discovery, (meta, workflow, origin) => {
      if (origin === undefined) unattributed.push(meta.name);
      // Input validation: a custom `validateInput` wins; otherwise build one from the class-validator
      // `inputSchema` DTO (lazy — class-validator is only required if a workflow uses inputSchema).
      const validateInput =
        meta.validateInput ??
        (meta.inputSchema ? classValidatorInput(meta.inputSchema) : undefined);
      const eventBatch = meta.debounce
        ? ({ mode: 'debounce', windowMs: parseDuration(meta.debounce) } as const)
        : meta.batch
          ? ({
              mode: 'batch',
              maxSize: meta.batch.maxSize,
              windowMs: parseDuration(meta.batch.within),
            } as const)
          : undefined;
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const workflowCtor: object = (workflow as any).constructor as object;
      this.engine.register(meta.name, meta.version, (ctx, input) => workflow.run(ctx, input), {
        // Stamped onto every run this workflow starts (see `WorkflowRun.origin`). Derived from the
        // class's declaring file, so a lib gets attributed without opting in; `undefined` when it
        // could not be derived, which the engine records as unknown rather than defaulting.
        origin,
        tags: meta.tags,
        singleton: meta.singleton,
        executionTimeout: meta.executionTimeout,
        requires: meta.requires,
        validateInput,
        searchAttributesSchema: meta.searchAttributes,
        onEvent: getOnEvents(meta, workflowCtor),
        eventBatch,
        // Uniform dispatch (opt-in): when an in-app worker is configured, register the body GROUP-SERVED
        // so the engine dispatches its turns over the transport instead of running it inline; the
        // co-located worker consumer (Task 5: one queue PER REGISTERED NAME) replays the same body.
        // The routing token — and the executor that dispatches under it — MUST be keyed by THIS
        // workflow's own name (`tenantGroup(sanitizeQueueToken(meta.name), partition)`), not a single
        // group shared across every discovered `@Workflow`: a fixed shared token would dispatch every
        // workflow's turns to one queue while the co-located worker subscribes one queue per name,
        // so a turn dispatched under the wrong token would never be consumed. Absent → the inline fast
        // path (unchanged).
        ...(this.inAppWorker
          ? {
              group: tenantGroup(sanitizeQueueToken(meta.name), this.inAppWorker.partition),
              executor: new RemoteWorkflowExecutor(
                this.inAppWorker.transport,
                meta.name,
                this.inAppWorker.partition,
              ),
            }
          : {}),
      });

      // Class-first statics (`MyWorkflow.start()` / `.execute()`): bind the class to THIS engine,
      // so the statics resolve the engine that registered it — no global engine singleton.
      bindWorkflowClass(workflowCtor as new () => object, {
        start: (name, input, runId, opts) => this.engine.start(name, input, runId, opts),
        waitForRun: (runId, opts) => this.engine.waitForRun(runId, opts),
      });

      const inline = this.findDeadLetterHandler(workflow as unknown as object);
      if (inline && meta.deadLetterWorkflow) {
        // Two dead-letter targets for one workflow is ambiguous config, not a precedence question —
        // fail fast at boot rather than silently picking one.
        throw new Error(
          `@Workflow ${meta.name} declares both an inline @DeadLetter() method and a deadLetterWorkflow option. Use one: the inline handler, or the reference.`,
        );
      }
      if (inline) {
        // The inline handler is itself a durable workflow, registered as `<name>.dlq`, so it gets
        // checkpointing and a dashboard run linked to the dead run via the `dlq:<runId>` id.
        const dlqName = `${meta.name}.dlq`;
        // Declared on the very same class, so it carries the same origin.
        this.engine.register(dlqName, meta.version, inline, { origin });
        deadLetterByWorkflow.set(meta.name, dlqName);
      } else if (meta.deadLetterWorkflow) {
        deadLetterByWorkflow.set(meta.name, workflowName(meta.deadLetterWorkflow));
      }
    });

    this.reportUnattributed(unattributed);
    this.installDeadLetterRouting(deadLetterByWorkflow);
  }

  /**
   * Says, once at boot, which workflows could not be attributed to a package — their runs are stamped
   * with no `origin` at all, so an operator filtering the dashboard by origin will not see them. That
   * is the deliberate trade (an absent origin over an invented one), but it has to be visible: a
   * silently-dropped run is indistinguishable from a run that never happened.
   */
  private reportUnattributed(names: string[]): void {
    if (names.length === 0) return;
    console.warn(
      `[nestjs-durable] could not derive an origin (declaring package) for ${names.length} workflow(s): ${names.join(', ')}. Their runs carry no origin and will not match an origin filter.`,
    );
  }

  /** Returns the instance's `@DeadLetter()` method bound to the instance, or undefined if none. */
  private findDeadLetterHandler(instance: object): WorkflowFn | undefined {
    const prototype = Object.getPrototypeOf(instance);
    for (const methodName of this.metadataScanner.getAllMethodNames(prototype)) {
      const method = (instance as Record<string, unknown>)[methodName];
      if (typeof method === 'function' && isDeadLetterHandler(method)) {
        return (ctx, input) => method.call(instance, ctx, input);
      }
    }
    return undefined;
  }

  /**
   * Installs a single onDead listener that routes a dead run to its workflow's handler (from the
   * map) or the module-level `deadLetterWorkflow` default. The handler is started idempotently with
   * a `dlq:<runId>` id, so re-recovery never double-dispatches.
   */
  private installDeadLetterRouting(byWorkflow: Map<string, string>): void {
    const fallback = this.options.deadLetterWorkflow
      ? workflowName(this.options.deadLetterWorkflow)
      : undefined;
    if (byWorkflow.size === 0 && !fallback) return;
    this.engine.onDead((run: WorkflowRun) => {
      const target = byWorkflow.get(run.workflow) ?? fallback;
      if (!target) return;
      void this.engine
        .start(
          target,
          { deadRunId: run.id, workflow: run.workflow, input: run.input, error: run.error },
          `dlq:${run.id}`,
          // Route the dead-letter handler to the dead run's OWN tenant so an operator dispatches it to
          // that tenant's worker group (`target@<tenant>`), not the bare group.
          { namespace: run.namespace },
        )
        .catch(() => undefined);
    });
  }
}
